// Napojenie banky cez Enable Banking — infraštruktúra tenanta (Administrácia).
// Fakturácia je len prvý konzument spárovaných pohybov; ďalší bude modul
// schvaľovania (párovanie debetov na došlé faktúry).
import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';

const btnSmall: React.CSSProperties = { padding: '5px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 12 };

// ---------------------------------------------------------------
// Napojená banka (Enable Banking) — automatické sťahovanie pohybov.
// Pripojenie = SCA v banke (full-page redirect, návrat na /admin).
// ---------------------------------------------------------------
const BankConnections: React.FC<{ notify: (m: string) => void; onSynced: () => void }> = ({ notify, onSynced }) => {
  const [connections, setConnections] = useState<any[]>([]);
  const [banks, setBanks] = useState<any[]>([]);
  const [pickedBank, setPickedBank] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(() => {
    api.get('/invoicing/bank/connections')
      .then(r => setConnections(r.data?.connections || []))
      .catch((e: any) => { if (e.response?.status === 403) setForbidden(true); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const openPicker = async () => {
    setShowPicker(true);
    if (banks.length) return;
    try {
      const r = await api.get('/invoicing/bank/banks', { params: { country: 'SK' } });
      setBanks(r.data?.banks || []);
    } catch (e: any) {
      notify(e.response?.data?.error || 'Zoznam bánk sa nepodarilo načítať');
      setShowPicker(false);
    }
  };

  const connect = async () => {
    if (!pickedBank) return;
    setBusy(true);
    try {
      const r = await api.post('/invoicing/bank/connections', { bank: pickedBank, country: 'SK' });
      // Full-page redirect na SCA banky; banka nás vráti na /invoices
      window.location.href = r.data.url;
    } catch (e: any) {
      notify(e.response?.data?.error || 'Pripojenie zlyhalo');
      setBusy(false);
    }
  };

  const syncNow = async (c: any) => {
    setSyncingId(c.id);
    try {
      const r = await api.post(`/invoicing/bank/connections/${c.id}/sync`);
      notify(`${c.bank_name}: ${r.data.imported} nových pohybov, ${r.data.matched} spárovaných`);
      load();
      onSynced();
    } catch (e: any) {
      notify(e.response?.data?.error || 'Sync zlyhal');
      load();
    } finally {
      setSyncingId(null);
    }
  };

  const disconnect = async (c: any) => {
    if (!window.confirm(`Odpojiť ${c.bank_name}? Stiahnuté pohyby v systéme ostanú.`)) return;
    try {
      await api.delete(`/invoicing/bank/connections/${c.id}`);
      load();
    } catch (e: any) {
      notify(e.response?.data?.error || 'Odpojenie zlyhalo');
    }
  };

  // Účtovník spojenia nespravuje (403) — sekciu mu neukazujeme vôbec.
  if (forbidden) return null;
  const visible = connections.filter(c => c.status !== 'revoked');

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Napojená banka — automatické úhrady
        </div>
        {!showPicker && (
          <button onClick={openPicker} style={btnSmall}>+ Pripojiť banku</button>
        )}
      </div>

      {showPicker && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={pickedBank} onChange={e => setPickedBank(e.target.value)}
            style={{ padding: '5px 8px', fontSize: 12.5, border: '1px solid #d1d5db', borderRadius: 5, maxWidth: 280 }}>
            <option value="">— vyber banku —</option>
            {banks.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
          </select>
          <button onClick={connect} disabled={!pickedBank || busy} style={{ ...btnSmall, opacity: !pickedBank || busy ? 0.6 : 1 }}>
            {busy ? 'Presmerúvam…' : 'Autorizovať v banke'}
          </button>
          <button onClick={() => setShowPicker(false)} style={{ ...btnSmall, border: 'none' }}>zrušiť</button>
          <div style={{ fontSize: 11, color: '#6b7280', width: '100%' }}>
            Presmerujeme ťa do banky, kde prístup k účtom potvrdíš (platí ~90–180 dní, potom sa obnovuje).
          </div>
        </div>
      )}

      {visible.length === 0 && !showPicker && (
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>
          Žiadna banka nie je napojená — pohyby vieš zatiaľ importovať ručne vo Fakturácii → Úhrady z banky.
        </div>
      )}

      {visible.map(c => (
        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12.5, flexWrap: 'wrap' }}>
          <b>{c.bank_name}</b>
          {c.status === 'active' && <span style={{ color: '#059669', fontSize: 11.5 }}>● aktívne</span>}
          {c.status === 'pending' && <span style={{ color: '#d97706', fontSize: 11.5 }}>● čaká na autorizáciu</span>}
          {c.status === 'expired' && (
            <span style={{ color: '#dc2626', fontSize: 11.5 }}>
              ● súhlas vypršal — <button onClick={openPicker} style={{ ...btnSmall, color: '#dc2626' }}>pripojiť znova</button>
            </span>
          )}
          <span style={{ color: '#6b7280', fontSize: 11.5 }}>
            {(c.accounts || []).map((a: any) => a.iban).filter(Boolean).join(', ')}
          </span>
          {c.last_sync_at && (
            <span style={{ color: '#9ca3af', fontSize: 11 }}>
              sync {new Date(c.last_sync_at).toLocaleString('sk-SK')}
            </span>
          )}
          {c.status === 'active' && (
            <button onClick={() => syncNow(c)} disabled={syncingId === c.id}
              style={{ ...btnSmall, opacity: syncingId === c.id ? 0.6 : 1 }}>
              {syncingId === c.id ? 'Sťahujem…' : '↓ Stiahnuť pohyby'}
            </button>
          )}
          <button onClick={() => disconnect(c)} style={{ ...btnSmall, border: 'none', color: '#9ca3af' }}>odpojiť</button>
        </div>
      ))}
      {visible.some(c => c.last_error && c.status === 'active') && (
        <div style={{ fontSize: 11.5, color: '#dc2626', marginTop: 6 }}>
          Posledný sync hlásil chybu: {visible.find(c => c.last_error)?.last_error}
        </div>
      )}
    </div>
  );
};

export default BankConnections;
